import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI, organizationAPI } from '../api';

export const OrgContext = createContext(null);

const LS_KEY = 'zamanat_active_org_id';

export const OrgProvider = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [activeOrg, setActiveOrg] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [allOrgsForSuperAdmin, setAllOrgsForSuperAdmin] = useState([]);
  // True while we're still resolving the active org on load. Consumers (e.g. the Sidebar logo)
  // gate on this so they reserve space instead of flashing the default Zamanat logo before the
  // org's own logo resolves.
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = useMemo(
    () => user?.primary_role === 'super_admin' || user?.roles?.includes('super_admin'),
    [user]
  );

  const memberOrganizations = useMemo(() => user?.organizations || [], [user]);

  // For super admin: fetch all platform orgs
  useEffect(() => {
    if (!isAuthenticated || !isSuperAdmin) {
      setAllOrgsForSuperAdmin([]);
      return;
    }
    setLoading(true);
    organizationAPI.list({ limit: 100 }).then((res) => {
      const items = (res?.data || []).map((org) => ({
        id: org.id,
        name: org.name,
        slug: org.slug,
        logo_url: org.logo_url,
        favicon_url: org.favicon_url,
        role: 'super_admin',
      }));
      setAllOrgsForSuperAdmin(items);
      // No orgs to resolve → stop gating. Otherwise the init effect below flips loading
      // off once switchOrg settles.
      if (items.length === 0) setLoading(false);
    }).catch(() => { setAllOrgsForSuperAdmin([]); setLoading(false); });
  }, [isAuthenticated, isSuperAdmin]);

  const organizations = useMemo(
    () => (isSuperAdmin ? allOrgsForSuperAdmin : memberOrganizations),
    [isSuperAdmin, allOrgsForSuperAdmin, memberOrganizations]
  );

  // Super admin: set activeOrg once the platform org list is loaded
  // (user.active_organization is not on the user object — it lives at the top of the
  // login response which AuthContext discards, so we resolve from the fetched list)
  useEffect(() => {
    if (!isSuperAdmin || !isAuthenticated || allOrgsForSuperAdmin.length === 0) return;
    // Don't override an org set by switchOrg — only init when nothing is active
    if (activeOrg) { setLoading(false); return; }

    const storedOrgId = localStorage.getItem(LS_KEY);
    const storedMatch = storedOrgId && allOrgsForSuperAdmin.find((o) => o.id === storedOrgId);
    const resolved = storedMatch || allOrgsForSuperAdmin[0];
    switchOrg(resolved.id).finally(() => setLoading(false));
  // activeOrg intentionally omitted — only trigger when the orgs list arrives
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, isAuthenticated, allOrgsForSuperAdmin]);

  // Regular users: restore active org on mount / user change
  useEffect(() => {
    // Wait for auth to finish loading before resolving the org. On a full-page reload
    // `user` is momentarily null while getCurrentUser() is in flight; flipping loading
    // off here would let ProtectedRoute redirect to /select-organization before the
    // active org is restored from localStorage.
    if (authLoading) return;

    if (!isAuthenticated || !user) {
      setActiveOrg(null);
      setLoading(false);
      return;
    }

    if (isSuperAdmin) return; // handled by the effect above (incl. its loading gate)

    const storedOrgId = localStorage.getItem(LS_KEY);

    if (memberOrganizations.length === 1) {
      setActiveOrg(memberOrganizations[0]);
      localStorage.setItem(LS_KEY, memberOrganizations[0].id);
    } else if (storedOrgId && memberOrganizations.length > 0) {
      const match = memberOrganizations.find((o) => o.id === storedOrgId);
      if (match) setActiveOrg(match);
    }
    // Resolution for regular users is synchronous from the user object — done now.
    setLoading(false);
  }, [authLoading, isAuthenticated, user, memberOrganizations, isSuperAdmin]);

  const switchOrg = useCallback(
    async (orgId) => {
      setSwitching(true);
      try {
        const res = await authAPI.switchOrg(orgId);
        const org = res.active_organization;
        setActiveOrg(org);
        localStorage.setItem(LS_KEY, org.id);
        return org;
      } finally {
        setSwitching(false);
      }
    },
    []
  );

  const clearOrg = useCallback(() => {
    setActiveOrg(null);
    localStorage.removeItem(LS_KEY);
  }, []);

  const isOrgSelected = !!activeOrg;

  const value = {
    organizations,
    activeOrg,
    isOrgSelected,
    isSuperAdmin,
    switching,
    loading,
    switchOrg,
    clearOrg,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};
